import { COMPANY } from '../constants/index';
import { getAppMode } from '../saas/appMode';
import { BRAND } from '../saas/config/landingContent';
import { isSaasPlatformHost } from '../share/serviceappBrand';
import { getPlatformDefaultBranding, type ResolvedCompanyBranding } from './companyBranding';

export const SAAS_SUPPORT_TICKET_PATH = '/support';

export type ErrorSurfaceBranding = {
  productName: string;
  headline: string;
  supportEmail: string;
  supportPhone: string | null;
  displayPhone: string | null;
  /** Tenant workspace ticket inbox. Platform crash help — never a tenant phone. */
  supportTicketUrl: string | null;
  logoUrl: string | null;
  variant: 'tb-site' | 'saas-platform';
};

function formatSupportPhone(phone: string): string {
  const cleaned = phone.replace(/\s+/g, '');
  if (cleaned.startsWith('+91') && cleaned.length === 13) {
    return `+91 ${cleaned.slice(3, 8)} ${cleaned.slice(8)}`;
  }
  if (cleaned.startsWith('+')) {
    return cleaned;
  }
  return phone;
}

export function isSaasErrorSurface(): boolean {
  if (typeof window === 'undefined') return getAppMode() === 'saas-platform';
  return getAppMode() === 'saas-platform' || isSaasPlatformHost(window.location.hostname);
}

let runtimeTenantErrorBranding: Pick<ErrorSurfaceBranding, 'productName' | 'logoUrl'> | null = null;
let runtimePlatformErrorBranding: Partial<ErrorSurfaceBranding> | null = null;
let runtimePublicMarketingErrorBranding: ErrorSurfaceBranding | null = null;

/** Super-admin platform support contact for ErrorBoundary (SaaS marketing + workspace). */
export function setPlatformErrorSurfaceBranding(input: {
  productName?: string;
  supportEmail?: string;
  supportPhone?: string | null;
  displayPhone?: string | null;
  logoUrl?: string | null;
}): void {
  runtimePlatformErrorBranding = {
    productName: input.productName,
    supportEmail: input.supportEmail,
    supportPhone: input.supportPhone ?? null,
    displayPhone: input.displayPhone ?? null,
    logoUrl: input.logoUrl ?? undefined,
  };
}

/**
 * Tenant visual identity only. Company Profile / workspace phones must never
 * appear on the platform crash screen — those numbers belong to the tenant,
 * not ServiceApp support.
 */
export function setErrorSurfaceTenantBranding(branding: ResolvedCompanyBranding): void {
  const defaults = getPlatformDefaultBranding();
  runtimeTenantErrorBranding = {
    productName: branding.companyName?.trim() || defaults.companyName,
    logoUrl: branding.logoUrl ?? defaults.logoUrl,
  };
}

export function clearErrorSurfaceTenantBranding(): void {
  runtimeTenantErrorBranding = null;
}

/** Marketing-site tenant contact for ErrorBoundary (non-SaaS public pages). */
export function setPublicMarketingErrorSurfaceBranding(input: {
  companyName?: string;
  supportEmail?: string;
  supportPhone?: string | null;
}): void {
  const phone = input.supportPhone?.trim() || '';
  runtimePublicMarketingErrorBranding = {
    productName: input.companyName?.trim() || COMPANY.NAME,
    headline: 'We hit a snag, but we have your back',
    supportEmail: input.supportEmail?.trim() || COMPANY.EMAIL,
    supportPhone: phone || null,
    displayPhone: phone ? formatSupportPhone(phone) : null,
    supportTicketUrl: null,
    logoUrl: null,
    variant: 'tb-site',
  };
}

export function clearPublicMarketingErrorSurfaceBranding(): void {
  runtimePublicMarketingErrorBranding = null;
}

function getSaasPlatformErrorBranding(): ErrorSurfaceBranding {
  const platform = runtimePlatformErrorBranding;
  return {
    productName: platform?.productName?.trim() || BRAND.name,
    headline: 'Something went wrong — we\u2019re on it',
    supportEmail: platform?.supportEmail?.trim() || BRAND.contactEmail,
    supportPhone: platform?.supportPhone ?? null,
    displayPhone: platform?.displayPhone ?? null,
    supportTicketUrl: SAAS_SUPPORT_TICKET_PATH,
    logoUrl: platform?.logoUrl ?? BRAND.logo,
    variant: 'saas-platform',
  };
}

export function getErrorSurfaceBranding(): ErrorSurfaceBranding {
  if (isSaasErrorSurface()) {
    const platform = getSaasPlatformErrorBranding();
    const tenant = runtimeTenantErrorBranding;
    if (!tenant) return platform;
    return {
      ...platform,
      productName: tenant.productName || platform.productName,
      logoUrl: tenant.logoUrl ?? platform.logoUrl,
    };
  }

  if (runtimePublicMarketingErrorBranding) {
    return runtimePublicMarketingErrorBranding;
  }

  const phone = COMPANY.PHONE || '+919995216660';
  return {
    productName: COMPANY.NAME,
    headline: 'We hit a snag, but we have your back',
    supportEmail: COMPANY.EMAIL,
    supportPhone: phone,
    displayPhone: formatSupportPhone(phone),
    supportTicketUrl: null,
    logoUrl: null,
    variant: 'tb-site',
  };
}

export function resetErrorSurfaceBrandingForTests(): void {
  runtimeTenantErrorBranding = null;
  runtimePlatformErrorBranding = null;
  runtimePublicMarketingErrorBranding = null;
}
